import * as THREE from 'three/webgpu';
import { attribute } from 'three/tsl';

import { dayNightInstanceTint, dayNightVertexColor, hitFlashMix } from '../render/nodes.js';

/**
 * @file One instanced draw call for ONE part geometry — the unit of the rig
 * renderer's batching. `RigEnemyRenderer` keeps one of these per part slot per
 * type and pushes every live unit's world-matrix for that part into it, so
 *
 *   draw calls = number of distinct part geometries alive ≠ number of units.
 *
 * The material is the same node composition `MeshEnemyRenderer` used (and this
 * file inherits its two hard-won rules):
 *
 *   colorNode = hitFlashMix(iFlash, vertexBake × mix(iColD, iColN, uNight))
 *
 *  • `vertexColors` stays FALSE — `dayNightVertexColor` reads `vertexColor()`
 *    itself, and r185 would multiply the `color` attribute in a second time.
 *  • Instance data is written straight into the typed arrays by the caller
 *    (hand-written column-major TRS) — `setMatrixAt` costs a Quaternion, a
 *    Vector3 and a copy per part per unit per step, and with parts the N is
 *    bigger than it ever was for whole enemies.
 *
 * @module engine/actors/PartBatch
 */

export class PartBatch {
  /**
   * @param {Object} o
   * @param {THREE.BufferGeometry} o.geometry Non-indexed, baked (`color` +
   *   `color1`). NOT owned — the rig backend owns part geometries.
   * @param {number} [o.max=512] Instance cap; match the manager's pool.
   * @param {string} [o.name='part']
   */
  constructor(o) {
    const { geometry, max = 512, name = 'part' } = o;

    /** @type {THREE.InstancedBufferAttribute} */
    this.colD = new THREE.InstancedBufferAttribute(new Float32Array(max * 3).fill(1), 3);
    /** @type {THREE.InstancedBufferAttribute} */
    this.colN = new THREE.InstancedBufferAttribute(new Float32Array(max * 3), 3);
    /** @type {THREE.InstancedBufferAttribute} */
    this.flash = new THREE.InstancedBufferAttribute(new Float32Array(max), 1);
    this.colD.setUsage(THREE.DynamicDrawUsage);
    this.colN.setUsage(THREE.DynamicDrawUsage);
    this.flash.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('iColD', this.colD);
    geometry.setAttribute('iColN', this.colN);
    geometry.setAttribute('iFlash', this.flash);

    /** @type {THREE.MeshBasicNodeMaterial} */
    this.material = new THREE.MeshBasicNodeMaterial();
    this.material.name = name;
    this.material.fog = true;
    // NEVER true on a material whose colorNode reads vertexColor() itself.
    this.material.vertexColors = false;
    this.material.colorNode = hitFlashMix(
      attribute('iFlash', 'float'),
      dayNightVertexColor().mul(
        dayNightInstanceTint(attribute('iColD', 'vec3'), attribute('iColN', 'vec3')),
      ),
    );

    /** @type {THREE.InstancedMesh} */
    this.mesh = new THREE.InstancedMesh(geometry, this.material, max);
    this.mesh.name = name;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Every instance moves every step; a per-step bounding sphere costs more
    // than the culling it buys.
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrix();
    // Untouched slots must draw NOTHING — InstancedMesh fills with identity,
    // which is a full-size part at the world origin.
    this.mesh.instanceMatrix.array.fill(0);

    /** Direct write target for the hand-rolled TRS (16 floats per slot). */
    this.mArr = /** @type {Float32Array} */ (this.mesh.instanceMatrix.array);
    this._max = max;
    this._disposed = false;
  }

  /** Starts a frame: forget every instance. */
  reset() {
    this.mesh.count = 0;
  }

  /**
   * Claims the next instance slot.
   * @returns {number} Slot index, or -1 when the batch is full.
   */
  alloc() {
    const slot = this.mesh.count;
    if (slot >= this._max) return -1;
    this.mesh.count = slot + 1;
    return slot;
  }

  /** Ends a frame: upload what was pushed, hide the mesh when empty. */
  commit() {
    this.mesh.instanceMatrix.needsUpdate = true;
    this.colD.needsUpdate = true;
    this.colN.needsUpdate = true;
    this.flash.needsUpdate = true;
    this.mesh.visible = this.mesh.count > 0;
  }

  /** Idempotent. Does NOT dispose the geometry — the rig backend owns it. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.mesh.removeFromParent();
    this.mesh.dispose();
    this.material.dispose();
  }
}
